import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';
import { createSlug } from '../../util/urlHelpers';
import {
  getBrandDisplayName,
  getListingMoney,
  computeBundlePricing,
} from '../../util/bundlePricing';
import { ResponsiveImage, PrimaryButton, IconArrowHead, NamedLink } from '../../components';
import css from './SectionBundles.module.css';

const { Money } = sdkTypes;

const EMPTY = {};

const BundleProductCard = ({ product, listing, money, intl, config }) => {
  const firstImage = listing?.images?.[0] || null;
  const variantPrefix = config?.layout?.listingImage?.variantPrefix || 'listing-card';
  const title = listing?.attributes?.title || '…';
  const formatted = money ? formatMoney(intl, money) : null;
  const listingId = listing?.id?.uuid;
  const slug = listingId && title ? createSlug(title) : null;
  const listingConfig = config?.listing;
  const brandDisplayName = getBrandDisplayName(
    listing?.attributes?.publicData?.brand,
    listingConfig
  );

  const inner = (
    <>
      <div className={css.productImageWrapper}>
        <ResponsiveImage
          rootClassName={css.productImage}
          alt={title}
          image={firstImage}
          variants={[
            `${variantPrefix}`,
            `${variantPrefix}-2x`,
            'listing-gallery',
            'listing-gallery-2x',
          ]}
        />
      </div>
      <div className={css.productInfo}>
        {brandDisplayName ? (
          <div className={css.productBrand}>{brandDisplayName}</div>
        ) : null}
        <span className={css.productTitle} title={title}>
          {title}
        </span>
        {formatted ? <span className={css.productPrice}>{formatted}</span> : null}
      </div>
    </>
  );

  if (listingId && slug) {
    return (
      <NamedLink
        className={css.productColumn}
        name="ListingPage"
        params={{ id: listingId, slug }}
      >
        {inner}
      </NamedLink>
    );
  }

  return <div className={css.productColumn}>{inner}</div>;
};

const BundleCard = ({
  bundle,
  productListingsById,
  onAddBundleToCart,
  intl,
  config,
  isAdding,
}) => {
  const pricing = useMemo(
    () => computeBundlePricing(bundle, productListingsById),
    [bundle, productListingsById]
  );

  const finalMoney =
    pricing.currency && Number.isFinite(pricing.finalAmount)
      ? (() => {
          try {
            return new Money(pricing.finalAmount, pricing.currency);
          } catch {
            return null;
          }
        })()
      : null;

  const rawMoney =
    pricing.showStrikethrough && pricing.currency && Number.isFinite(pricing.rawSubtotal)
      ? (() => {
          try {
            return new Money(pricing.rawSubtotal, pricing.currency);
          } catch {
            return null;
          }
        })()
      : null;

  const bundleBadgeDiscountFormatted =
    pricing.badgeDiscountSubunits != null &&
    pricing.badgeDiscountSubunits > 0 &&
    pricing.currency
      ? (() => {
          try {
            return formatMoney(intl, new Money(pricing.badgeDiscountSubunits, pricing.currency));
          } catch {
            return null;
          }
        })()
      : null;

  const disabled = pricing.hasMissingProducts || isAdding || !finalMoney;
  const isMultiProduct = pricing.productsWithListings.length > 2;

  return (
    <article className={classNames(css.bundleCard, { [css.bundleCardGrid]: isMultiProduct })}>
      <header className={css.bundleHeader}>
        <h3 className={css.bundleTitle}>{bundle.title}</h3>
        {bundle.description ? (
          <p className={css.bundleDescription}>{bundle.description}</p>
        ) : null}
      </header>

      {isMultiProduct ? (
        // Grid layout for packs with more than 2 products: rows of 2 with "+" between items
        (() => {
          const products = pricing.productsWithListings;
          const rows = [];
          for (let i = 0; i < products.length; i += 2) {
            rows.push(products.slice(i, i + 2));
          }
          return (
            <div className={css.productPackGrid}>
              {rows.map((row, rowIdx) => (
                <React.Fragment key={`row-${rowIdx}`}>
                  {rowIdx > 0 ? (
                    <div className={css.gridRowSeparator}>
                      <span className={css.eqSymbolPlus} aria-hidden="true">
                        +
                      </span>
                    </div>
                  ) : null}
                  <div className={css.gridRow}>
                    {row.map(({ product, listing, money }, colIdx) => {
                      const qty = Math.max(1, Number(product.quantity) || 1);
                      const globalIdx = rowIdx * 2 + colIdx;
                      return (
                        <React.Fragment key={`${bundle.id}-${product.id}-${globalIdx}`}>
                          {colIdx > 0 ? (
                            <span className={css.eqSymbolPlus} aria-hidden="true">
                              +
                            </span>
                          ) : null}
                          {qty > 1 ? (
                            <span className={css.eqSymbolMult} aria-hidden="true">
                              {qty}
                              {'\u00a0'}×
                            </span>
                          ) : null}
                          <BundleProductCard
                            product={product}
                            listing={listing}
                            money={money}
                            intl={intl}
                            config={config}
                          />
                        </React.Fragment>
                      );
                    })}
                  </div>
                </React.Fragment>
              ))}
            </div>
          );
        })()
      ) : (
        <div className={css.productPack}>
          {pricing.productsWithListings.map(({ product, listing, money }, idx) => {
            const qty = Math.max(1, Number(product.quantity) || 1);
            return (
              <React.Fragment key={`${bundle.id}-${product.id}-${idx}`}>
                {idx > 0 ? (
                  <span className={css.eqSymbolPlus} aria-hidden="true">
                    +
                  </span>
                ) : null}
                {qty > 1 ? (
                  <span className={css.eqSymbolMult} aria-hidden="true">
                    {qty}
                    {'\u00a0'}×
                  </span>
                ) : null}
                <BundleProductCard
                  product={product}
                  listing={listing}
                  money={money}
                  intl={intl}
                  config={config}
                />
              </React.Fragment>
            );
          })}
        </div>
      )}

      <div className={css.bundleFooter}>
        <div className={css.bundlePriceBlock}>
          {finalMoney && pricing.showStrikethrough && rawMoney ? (
            <div className={css.bundlePriceWithSale}>
              <p className={css.bundlePriceMain}>
                <span className={css.bundlePriceValue}>{formatMoney(intl, finalMoney)}</span>
              </p>
              <div className={css.bundleOriginalPriceWrapper}>
                <div
                  className={classNames(css.bundleStrikethroughStack, {
                    [css.bundleStrikethroughStackWithBadge]: !!bundleBadgeDiscountFormatted,
                  })}
                >
                  {bundleBadgeDiscountFormatted ? (
                    <span className={css.bundleDiscountBadge}>-{bundleBadgeDiscountFormatted}</span>
                  ) : null}
                  <span className={css.bundleOldPrice}>{formatMoney(intl, rawMoney)}</span>
                </div>
              </div>
            </div>
          ) : finalMoney ? (
            <span className={css.bundlePrice}>{formatMoney(intl, finalMoney)}</span>
          ) : (
            <span className={css.bundlePricePlaceholder}>—</span>
          )}
        </div>
        <PrimaryButton
          className={css.bundleAddButton}
          onClick={() => !disabled && onAddBundleToCart(bundle)}
          disabled={disabled}
          inProgress={!!isAdding}
          type="button"
        >
          {intl.formatMessage({
            id: 'SectionBundles.addToCart',
            defaultMessage: 'Ajouter au panier',
          })}
        </PrimaryButton>
      </div>
    </article>
  );
};

const SectionBundles = ({
  bundles,
  productListingsById = EMPTY,
  onAddBundleToCart,
  intl,
  config,
  addingBundleId = null,
}) => {
  const scrollerRef = useRef(null);
  const [scrollState, setScrollState] = useState({ canScrollLeft: false, canScrollRight: true });

  if (!Array.isArray(bundles) || bundles.length === 0) return null;

  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
    const left = el.scrollLeft;
    setScrollState({
      canScrollLeft: left > 8,
      canScrollRight: left < maxScroll - 8,
    });
  }, []);

  useLayoutEffect(() => {
    if (bundles.length <= 1) return undefined;
    const el = scrollerRef.current;
    if (!el) return undefined;
    handleScroll();
    const ro = new ResizeObserver(() => handleScroll());
    ro.observe(el);
    window.addEventListener('resize', handleScroll);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', handleScroll);
    };
  }, [bundles.length, handleScroll]);

  const scrollBy = direction => {
    const el = scrollerRef.current;
    if (!el) return;
    const delta = Math.round(el.clientWidth * 0.85) * (direction === 'right' ? 1 : -1);
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  const heading = intl.formatMessage({
    id: 'SectionBundles.heading',
    defaultMessage: 'Faîtes-en plus avec nos packs',
  });

  const showArrows = bundles.length > 1;

  return (
    <section className={css.root} aria-label={heading}>
      <h2 className={css.sectionHeading}>{heading}</h2>

      <div className={css.carouselWrapper}>
        {showArrows ? (
          <button
            type="button"
            aria-label={intl.formatMessage({
              id: 'SectionBundles.previous',
              defaultMessage: 'Précédent',
            })}
            className={classNames(css.navButton, css.navButtonPrev, {
              [css.navButtonHidden]: !scrollState.canScrollLeft,
            })}
            onClick={() => scrollBy('left')}
          >
            <IconArrowHead direction="left" size="big" />
          </button>
        ) : null}

        <div
          className={classNames(css.bundlesCarousel, {
            [css.bundlesCarouselSingle]: bundles.length === 1,
          })}
          ref={scrollerRef}
          onScroll={handleScroll}
          role="list"
        >
          {bundles.map(bundle => (
            <div className={css.bundleCardWrapper} role="listitem" key={bundle.id}>
              <BundleCard
                bundle={bundle}
                productListingsById={productListingsById}
                onAddBundleToCart={onAddBundleToCart}
                intl={intl}
                config={config}
                isAdding={addingBundleId === bundle.id}
              />
            </div>
          ))}
        </div>

        {showArrows ? (
          <button
            type="button"
            aria-label={intl.formatMessage({
              id: 'SectionBundles.next',
              defaultMessage: 'Suivant',
            })}
            className={classNames(css.navButton, css.navButtonNext, {
              [css.navButtonHidden]: !scrollState.canScrollRight,
            })}
            onClick={() => scrollBy('right')}
          >
            <IconArrowHead direction="right" size="big" />
          </button>
        ) : null}
      </div>
    </section>
  );
};

export { BundleProductCard };
export default SectionBundles;
